<template>
    <div
        class="flex flex-col gap-4 min-h-0 overflow-auto scroll-thin pt-4 px-4 pb-4 md:pl-0 md:-mt-6 md:-mr-6 md:-mb-3 md:pb-3"
    >
        <div class="mb-2 md:hidden">
            <router-link :to="{ name: 'message-hub.intelligent-inbox' }" class="text-base">
                <font-awesome-icon widthAuto :icon="faArrowLeft"></font-awesome-icon>
                Intelligent Inbox
            </router-link>
        </div>

        <vx-box color="info" class="flex flex-col gap-4 p-6">
            <div>
                <span class="font-extrabold text-2xl text-slate-700"> Intelligent Inbox Settings </span>
                <p class="text-sm text-slate-500 mb-0">
                    Configure how the AI classifies and prioritizes conversations in your intelligent inbox.
                </p>
            </div>

            <intelligent-inbox-wizard-brand-context v-model="wizardData" :animated="false" :expandable="false" />
            <intelligent-inbox-wizard-opportunity-detection
                v-model="wizardData"
                :animated="false"
                :expandable="false"
            />
            <intelligent-inbox-wizard-status-definition v-model="wizardData" :animated="false" :expandable="false" />

            <div class="flex flex-col gap-4">
                <div>
                    <div class="font-extrabold text-slate-700 text-xl">Categories</div>
                    <div class="text-sm text-slate-500">Configure how the AI classifies conversations.</div>
                </div>
                <div class="flex flex-col gap-2">
                    <div ref="sortableEl" class="flex flex-col gap-2">
                        <div
                            v-for="category in categories"
                            :key="category.id"
                            class="category-draggable flex items-center"
                        >
                            <span class="category-handle cursor-move pr-4 py-4">
                                <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                            </span>
                            <intelligent-inbox-category-card
                                class="grow"
                                :category="category"
                                @edit="openEdit"
                                @delete="confirmRemove"
                            />
                        </div>
                    </div>
                    <div class="flex items-center">
                        <span class="pr-4 py-4 invisible">
                            <font-awesome-icon widthAuto :icon="faGripDotsVertical"></font-awesome-icon>
                        </span>
                        <vx-box class="p-4">
                            <vx-button size="lg" @click.stop="openCreate()">
                                New Category <font-awesome-icon :icon="faCirclePlus"></font-awesome-icon>
                            </vx-button>
                        </vx-box>
                    </div>
                </div>
            </div>
        </vx-box>

        <modal-intelligent-category
            v-model:visible="modalVisible"
            :categoryId="modalCategoryId"
        ></modal-intelligent-category>
    </div>
</template>

<script lang="ts" setup>
import { faArrowLeft, faCirclePlus, faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxSwal } from '@voxie/frontend-components';
import { useSortable } from '@vueuse/integrations/useSortable';
import { onMounted, ref } from 'vue';
import { useIntelligentInbox } from './useIntelligentInbox';
import type { IntelligentCategory } from './useIntelligentInbox';
import ModalIntelligentCategory from './ModalIntelligentCategory.vue';
import IntelligentInboxCategoryCard from './IntelligentInboxCategoryCard.vue';
import IntelligentInboxWizardBrandContext from './wizard/IntelligentInboxWizardBrandContext.vue';
import IntelligentInboxWizardOpportunityDetection from './wizard/IntelligentInboxWizardOpportunityDetection.vue';
import IntelligentInboxWizardStatusDefinition from './wizard/IntelligentInboxWizardStatusDefinition.vue';

const { categories, wizardData, removeCategory, reorderCategories } = useIntelligentInbox();

const modalVisible = ref(false);
const modalCategoryId = ref('');

const openCreate = (): void => {
    modalCategoryId.value = '';
    modalVisible.value = true;
};

const openEdit = (category: IntelligentCategory): void => {
    modalCategoryId.value = category.id;
    modalVisible.value = true;
};

const confirmRemove = async (category: IntelligentCategory): Promise<void> => {
    const result = await VxSwal.fire({
        title: 'Delete Category',
        text: `Are you sure you want to delete "${category.displayName}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: 'danger',
        confirmButtonText: 'Delete',
    });

    if (result.isConfirmed) {
        removeCategory(category.id);
    }
};

const sortableEl = ref(null);

const { start: startSortable } = useSortable(sortableEl, categories, {
    handle: '.category-handle',
    draggable: '.category-draggable',
    animation: 200,
    onUpdate: (e: { oldIndex: number; newIndex: number }) => {
        reorderCategories(e.oldIndex, e.newIndex);
    },
} as Record<string, unknown>);

onMounted(() => {
    startSortable();
});
</script>
